import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Modall from '../Modall'

const GET_STATUS_MEMBER_URL = 'admin/setting/status_member'
const CHANGE_STATUS_MEMBER_URL = 'admin/member/change_status_member'
const LEAVING_REQUEST_PREFIX = 'https://archivebureau.s3.eu-west-3.amazonaws.com/'
const STATUS_COMPLETION_DAYS_ID = 7
const STATUS_SUSPENDED_ID = 3
const STATUS_LEFT_ID = 5

const MemberStatuse = ({ showModal, handleColse, row, onSaved }) => {
	const [statuses, setStatuses] = useState([])
	const [selectedStatusId, setSelectedStatusId] = useState(row?.status?.statusId ?? null)
	const [suspentionReason, setSuspentionReason] = useState('')
	const [suspensionEndDate, setSuspensionEndDate] = useState('')
	const [leavingRequestFile, setLeavingRequestFile] = useState(null)
	const [leavingRequestName, setLeavingRequestName] = useState('')
	const [statusFieldsById, setStatusFieldsById] = useState({})
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(false)
	const lastSelectedStatusIdRef = useRef(null)

	useEffect(() => {
		if (!showModal) {
			return
		}

		setSelectedStatusId(row?.status?.statusId ?? null)
		const initialReason = row?.suspentionReason || ''
		const initialEndDate = (row?.suspensionEndDate || '').slice(0, 10)
		const initialLeavingRequestName = row?.leavingRequest || ''
		setSuspentionReason(initialReason)
		setSuspensionEndDate(initialEndDate)
		setLeavingRequestFile(null)
		setLeavingRequestName(initialLeavingRequestName)
		setStatusFieldsById((prev) => ({
			...prev,
			[row?.status?.statusId ?? '']: {
				suspentionReason: initialReason,
				suspensionEndDate: initialEndDate,
				leavingRequestFile: null,
				leavingRequestName: initialLeavingRequestName,
			},
		}))
		lastSelectedStatusIdRef.current = row?.status?.statusId ?? null
		setLoading(true)

		axios
			.get(GET_STATUS_MEMBER_URL)
			.then((response) => {
				setStatuses(response?.data || [])
			})
			.catch((error) => {
				console.error('Error fetching member statuses', error)
			})
			.finally(() => {
				setLoading(false)
			})
	}, [showModal, row])

	const selectedStatus = statuses.find((status) => status?.statusId === selectedStatusId)
	const isSuspendedOrCompletion =
		selectedStatusId === STATUS_SUSPENDED_ID || selectedStatusId === STATUS_COMPLETION_DAYS_ID
	const isLeftStatus = selectedStatusId === STATUS_LEFT_ID
	const isSaveDisabled = !selectedStatusId || saving
	const leavingRequestUrl = row?.leavingRequest
		? `${LEAVING_REQUEST_PREFIX}${row.leavingRequest}`
		: ''

	useEffect(() => {
		if (!selectedStatusId) {
			return
		}

		if (lastSelectedStatusIdRef.current === null) {
			lastSelectedStatusIdRef.current = selectedStatusId
			return
		}

		if (lastSelectedStatusIdRef.current === selectedStatusId) {
			return
		}

		const cachedFields = statusFieldsById[selectedStatusId]
		if (cachedFields) {
			setSuspentionReason(cachedFields.suspentionReason || '')
			setSuspensionEndDate(cachedFields.suspensionEndDate || '')
			setLeavingRequestFile(cachedFields.leavingRequestFile || null)
			setLeavingRequestName(cachedFields.leavingRequestName || '')
		} else if (isSuspendedOrCompletion) {
			setSuspentionReason('')
			setSuspensionEndDate('')
		} else if (isLeftStatus) {
			setLeavingRequestFile(null)
			setLeavingRequestName('')
		}

		lastSelectedStatusIdRef.current = selectedStatusId
	}, [selectedStatusId, isSuspendedOrCompletion, isLeftStatus, statusFieldsById])

	useEffect(() => {
		if (!selectedStatusId || !isSuspendedOrCompletion) {
			return
		}

		setStatusFieldsById((prev) => ({
			...prev,
			[selectedStatusId]: {
				suspentionReason,
				suspensionEndDate,
			},
		}))
	}, [selectedStatusId, isSuspendedOrCompletion, suspentionReason, suspensionEndDate])

	useEffect(() => {
		if (!selectedStatusId || !isLeftStatus) {
			return
		}

		setStatusFieldsById((prev) => ({
			...prev,
			[selectedStatusId]: {
				...(prev[selectedStatusId] || {}),
				leavingRequestFile,
				leavingRequestName,
			},
		}))
	}, [selectedStatusId, isLeftStatus, leavingRequestFile, leavingRequestName])

	const handleSave = async () => {
		if (!selectedStatusId || !row?.id) {
			return
		}

		setSaving(true)
		try {
			const reqStatusAction = {
				statusId: selectedStatusId,
				suspensionEndDate: suspensionEndDate || null,
				suspentionReason: suspentionReason || null,
			}

			const formData = new FormData()
			formData.append('reqStatusAction', JSON.stringify(reqStatusAction))
			if (isLeftStatus && leavingRequestFile) {
				formData.append('leavingRequest', leavingRequestFile)
			}

			await axios.post(`${CHANGE_STATUS_MEMBER_URL}/${row.id}`, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
			if (onSaved) {
				await onSaved()
			}
			handleColse()
		} catch (error) {
			console.error('Error changing member status', error)
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modall showModal={showModal}>
			<div className='w-full divide-y-2 divide-slate-500'>
				<div className='w-full bg-[#0d406b] flex justify-end'>
					<button
						className='p-1 px-2 m-3 border bg-slate-500 hover:bg-slate-200'
						onClick={handleColse}
					>
						x
					</button>
				</div>

				<div className='max-md:w-[600px] m-[30px auto] p-2 flex flex-col' style={{ direction: 'rtl' }}>
					<h3 className='font-bold text-center text-lg mb-4'>
						{`${row?.firstName || ''} ${row?.lastName || ''} - ${row?.number || ''}`}
					</h3>

					{loading && <p className='text-right'>טוען סטטוסים...</p>}

					{!loading && statuses.length === 0 && (
						<p className='text-right'>לא נמצאו סטטוסים להצגה</p>
					)}

					{!loading && statuses.length > 0 && (
						<div className='w-full flex gap-4'>
							<div className='w-1/2 flex flex-col gap-2'>
								{statuses.map((status) => (
									<label key={status?.statusId} className='flex items-center gap-3 border p-2'>
										<input
											type='radio'
											name='memberStatus'
											value={status?.statusId}
											checked={selectedStatusId === status?.statusId}
											onChange={() => setSelectedStatusId(status?.statusId)}
										/>
										<span>{status?.statusDescription}</span>
									</label>
								))}
							</div>
							<div className='w-1/2 border p-3 min-h-[140px]'>
								{!selectedStatusId && <p className='text-right'>בחר סטטוס להצגת פרטים</p>}
								{selectedStatusId && !isSuspendedOrCompletion && !isLeftStatus && (
									<p className='text-right'>אין שדות נוספים לסטטוס שנבחר כרגע</p>
								)}
								{selectedStatusId && isSuspendedOrCompletion && (
									<div className='flex flex-col gap-3'>
										<label className='text-right'>סיבת השעיה / השלמת ימים</label>
										<input
											type='text'
											className='border p-2 text-right'
											value={suspentionReason}
											onChange={(event) => setSuspentionReason(event.target.value)}
										/>
										<label className='text-right'>תאריך סיום</label>
										<div className='flex items-center gap-2'>
											<input
												type='date'
												className='border p-2 text-right flex-1'
												value={suspensionEndDate}
												onChange={(event) => setSuspensionEndDate(event.target.value)}
											/>
											<button
												type='button'
												className='border bg-slate-200 hover:bg-slate-300 p-2 text-xs'
												onClick={() => setSuspensionEndDate('')}
											>
												נקה
											</button>
										</div>
										{!suspensionEndDate && (
											<p className='text-right text-xs text-red-600'>פתוח ללא הגבלה</p>
										)}
									</div>
								)}
								{selectedStatusId && isLeftStatus && (
									<div className='flex flex-col gap-3'>
										<label className='text-right'>בקשת עזיבה (קובץ)</label>
										{leavingRequestUrl && (
											<a
												href={leavingRequestUrl}
												target='_blank'
												rel='noreferrer'
												className='text-right text-xs text-blue-700 underline'
											>
												צפייה בקובץ הקיים
											</a>
										)}
										<input
											type='file'
											className='border p-2 text-right'
											onChange={(event) => {
												const file = event.target.files?.[0] || null
												setLeavingRequestFile(file)
												setLeavingRequestName(file?.name || '')
											}}
										/>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				<div className='w-full p-2 flex justify-end gap-2'>
					<button
						className='border bg-green-500 hover:bg-green-300 p-1 px-3 disabled:opacity-50'
						disabled={isSaveDisabled}
						onClick={handleSave}
					>
						{saving ? 'שומר...' : 'שמור'}
					</button>
					<button className='border bg-slate-700 hover:bg-slate-500 p-1 px-3' onClick={handleColse}>
						סגור
					</button>
				</div>
			</div>
		</Modall>
	)
}

export default MemberStatuse
